import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Loader2, MapPin, Navigation, Calendar, Clock, CreditCard, CheckCircle2, User, Phone, Mail, LogOut } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { track } from "@/lib/track";

const DRAFT_KEY = "vlad_booking_draft";
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const baseInput =
  "w-full bg-[#0A0B0D] border border-white/10 focus:border-[#D4AF37] rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 text-sm outline-none transition-colors";

const GoogleG = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
  </svg>
);

const BookingWidget = () => {
  const { t, i18n } = useTranslation();
  const { user, login, logout } = useAuth();

  const [form, setForm] = useState(() => {
    try {
      // Restored only when returning from the Google login redirect; empty otherwise
      const pending = sessionStorage.getItem(DRAFT_KEY);
      if (pending) {
        sessionStorage.removeItem(DRAFT_KEY);
        return { ...JSON.parse(pending) };
      }
    } catch { /* ignore */ }
    return { name: "", email: "", phone: "", pickup: "", destination: "", pickupCoords: null, destCoords: null, date: todayStr(), time: "" };
  });
  const [price, setPrice] = useState(null);     // { price_label, price_eur, distance_km, duration_min }
  const [pricing, setPricing] = useState(false);
  const [priceErr, setPriceErr] = useState(null);
  const [paying, setPaying] = useState(false);
  const [notReady, setNotReady] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const reqRef = useRef(0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Save the trip ONLY across the Google login redirect (full-page), restored on return.
  // On a normal page load the fields start empty — no stale addresses to delete manually.
  const handleLogin = () => {
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
    login();
  };

  // Autofill from Google account (name + email; phone if remembered on profile)
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.name || "",
      email: f.email || user.email || "",
      phone: f.phone || user.phone || "",
    }));
  }, [user]);

  // Dynamic fare — computed in the background once both addresses are set
  const computePrice = useCallback(async (f) => {
    if (!f.pickup.trim() || !f.destination.trim()) { setPrice(null); setPriceErr(null); return; }
    const myReq = ++reqRef.current;
    setPricing(true); setPriceErr(null);
    try {
      const r = await axios.post(`${API}/estimate`, {
        pickup: f.pickup, destination: f.destination,
        pickup_coords: f.pickupCoords, dest_coords: f.destCoords, time: f.time || null,
      });
      if (myReq !== reqRef.current) return;
      if (r.data.ok) {
        setPrice(r.data);
        track("estimate_shown", { price: r.data.price_eur });
      } else {
        setPrice(null); setPriceErr(r.data.error);
      }
    } catch {
      if (myReq !== reqRef.current) return;
      setPrice(null); setPriceErr(t("home.priceError"));
    } finally {
      if (myReq === reqRef.current) setPricing(false);
    }
  }, [t]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!form.pickup.trim() || !form.destination.trim()) { setPrice(null); return; }
    debounceRef.current = setTimeout(() => computePrice(form), 700);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup, form.destination, form.pickupCoords, form.destCoords, form.time]);

  const canPay = form.name.trim() && form.phone.trim() && form.pickup.trim() &&
    form.destination.trim() && form.date && form.time && price && !paying;

  const reserveAndPay = async () => {
    if (!canPay) return;
    setPaying(true); setError(null); setNotReady(false);
    let bookingId = null;
    try {
      const b = await axios.post(`${API}/bookings`, {
        name: form.name, email: form.email, phone: form.phone,
        pickup: form.pickup, destination: form.destination,
        pickup_coords: form.pickupCoords, dest_coords: form.destCoords,
        date: form.date, time: form.time, language: i18n.language,
      });
      bookingId = b.data.id;
      track("form_submit", { type: "booking", price: b.data.price_eur });
      const p = await axios.post(`${API}/payments/create`, { booking_id: bookingId, origin: window.location.origin });
      if (p.data.hosted_checkout_url) {
        sessionStorage.removeItem(DRAFT_KEY);
        window.location.assign(p.data.hosted_checkout_url);
        return;
      }
      if (p.data.already_paid) { window.location.assign(`/payment/result?booking=${bookingId}`); return; }
      setError(t("home.payError"));
      setPaying(false);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 503) {
        setNotReady(true);
      } else if (status && status >= 500) {
        // 5xx / CDN edge errors (502/520/521…) return HTML, never show it raw
        setError("Service momentanément indisponible. Merci de réessayer dans un instant.");
      } else {
        setError(typeof detail === "string" && detail.length < 180 ? detail : t("home.payError"));
      }
      setPaying(false);
    }
  };

  return (
    <div data-testid="booking-widget" className="bg-[#0F1115] border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl">
      {/* Google one-tap sign-in area */}
      {user ? (
        <div data-testid="widget-user" className="flex items-center justify-between gap-3 mb-5 bg-[#0A0B0D] border border-white/8 rounded-xl px-4 py-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            {user.picture
              ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              : <span className="w-7 h-7 rounded-full bg-[#D4AF37]/20 flex items-center justify-center"><User size={14} className="text-[#D4AF37]" /></span>}
            <span className="text-white/80 text-sm truncate">{user.name || user.email}</span>
          </div>
          <button type="button" onClick={logout} data-testid="widget-logout" className="text-white/40 hover:text-white/80 transition-colors" aria-label="logout"><LogOut size={15} /></button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleLogin}
          data-testid="widget-google-login"
          className="w-full mb-5 flex items-center justify-center gap-2.5 bg-white hover:bg-white/90 text-[#1F1F1F] rounded-xl py-3 text-sm font-semibold transition-colors"
        >
          <GoogleG size={18} /> {t("home.googleContinue")}
        </button>
      )}

      {/* Trip */}
      <div className="space-y-3">
        <AddressAutocomplete
          value={form.pickup}
          onChange={(v) => set("pickup", v)}
          onSelect={(s) => setForm((f) => ({ ...f, pickup: s.value, pickupCoords: s.coords || null }))}
          placeholder={t("home.leadPickup")}
          testid="booking-pickup"
          label={t("home.leadPickup")}
          required
          icon={MapPin}
        />
        <AddressAutocomplete
          value={form.destination}
          onChange={(v) => set("destination", v)}
          onSelect={(s) => setForm((f) => ({ ...f, destination: s.value, destCoords: s.coords || null }))}
          placeholder={t("home.leadDestination")}
          testid="booking-destination"
          label={t("home.leadDestination")}
          required
          icon={Navigation}
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <Calendar size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37] pointer-events-none z-10" />
            <input type="date" min={todayStr()} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="booking-date" aria-label={t("home.bookDate")} className={`${baseInput} pr-2 date-input-enhanced`} />
          </div>
          <div className="relative">
            <Clock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37] pointer-events-none z-10" />
            <input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} data-testid="booking-time" aria-label={t("home.bookTime")} className={`${baseInput} pr-2 date-input-enhanced`} />
          </div>
        </div>

        {/* Contact (autofilled from Google) */}
        <div className="relative">
          <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
          <input type="text" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("home.leadName")} data-testid="booking-name" aria-label={t("home.leadName")} className={baseInput} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="relative">
            <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
            <input type="tel" required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("home.leadPhone")} data-testid="booking-phone" aria-label={t("home.leadPhone")} className={baseInput} />
          </div>
          <div className="relative">
            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={t("home.bookEmail")} data-testid="booking-email" aria-label={t("home.bookEmail")} className={baseInput} />
          </div>
        </div>
      </div>

      {/* Dynamic price (appears only after addresses are entered) */}
      <div className="mt-5" aria-live="polite">
        {pricing && (
          <div data-testid="booking-price-loading" className="flex items-center gap-2 text-white/60 text-sm py-3">
            <Loader2 size={15} className="animate-spin text-[#D4AF37]" /> {t("home.priceComputing")}
          </div>
        )}
        {!pricing && priceErr && (
          <div data-testid="booking-price-error" className="text-sm text-amber-400/90 bg-amber-400/10 border border-amber-400/20 rounded-xl px-4 py-2.5">{priceErr}</div>
        )}
        {!pricing && price && (
          <div data-testid="booking-price" className="flex items-end justify-between gap-3 bg-[#0A0B0D] border border-[#D4AF37]/30 rounded-2xl px-5 py-4 fade-up">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-0.5">{t("home.priceLabel")}</div>
              <div className="text-xs text-white/45">{price.distance_km} km · {price.duration_min} min · {t("home.priceAllInc")}</div>
            </div>
            <div data-testid="booking-price-value" className="font-serif text-4xl text-[#D4AF37] leading-none">{price.price_label}</div>
          </div>
        )}
        {!pricing && !price && !priceErr && (
          <p className="text-white/40 text-xs text-center py-2">{t("home.enterTripForPrice")}</p>
        )}
      </div>

      {error && <div data-testid="booking-error" className="mt-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-xl px-4 py-2.5">{error}</div>}
      {notReady && (
        <div data-testid="booking-payment-soon" className="mt-3 text-sm text-white/70 bg-white/5 border border-white/15 rounded-xl px-4 py-3">
          {t("home.paymentSoon")}
        </div>
      )}

      <button
        type="button"
        onClick={reserveAndPay}
        disabled={!canPay}
        data-testid="booking-pay-btn"
        className="w-full mt-5 bg-[#D4AF37] hover:bg-white text-[#0A0A0A] rounded-full py-4 text-sm font-semibold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {paying ? <><Loader2 size={17} className="animate-spin" /> {t("home.payRedirect")}</> : <><CreditCard size={17} /> {t("home.payNow")}</>}
      </button>
      <div className="flex items-center justify-center gap-1.5 mt-3 text-[11px] text-white/35">
        <CheckCircle2 size={12} className="text-[#D4AF37]" /> {t("home.paySecure")}
      </div>
    </div>
  );
};

export default BookingWidget;
